import * as THREE from "three";

import Sizes from "./Utils/Sizes.js";
import Time from "./Utils/Time.js";
import Camera from "./Camera.js";
import Renderer from "./Renderer.js";
import EventManager from "./EventManager.js";
import MenuManager from "./World/Menu/MenuManager.js";
import Room from "./World/Room.js";
import Resources from "./Utils/Resources.js";
import Debug from "./Utils/Debug.js";
import sources from "./sources.js";
import ObjectManager from "./World/Objects/managers/ObjectManager.js";
import Measures from "./World/Measures.js";
/* import ObjectsControl from './World/Objects/ObjectsControl.js'; */

let instance = null;

export default class Experience {
  constructor(_canvas) {
    // Singleton
    if (instance) {
      return instance;
    }
    instance = this;

    // Global access
    window.experience = this;

    // Options
    this.canvas = _canvas;

    // Raycaster
    this.raycaster = new THREE.Raycaster();

    // Loader
    this.textureLoader = new THREE.TextureLoader();

    // Setup
    this.debug = new Debug();
    this.sizes = new Sizes();
    this.time = new Time();
    this.scene = new THREE.Scene();
    this.sizeScene = 10;
    this.active = true;
    /* this.scene.add(new THREE.GridHelper(10)); */

    this.resources = new Resources(sources);
    this.camera = new Camera();
    this.renderer = new Renderer();
    this.objectManager = new ObjectManager();

    /*  this.objectsControl = new ObjectsControl() */

    // Resize event
    this.sizes.on("resize", () => {
      this.resize();
    });

    // Time tick event
    this.time.on("tick", () => {
      this.update();
    });
  }

  /**
   * Create Room with data in form
   */
  async createRoom(
    shape,
    arrayWalls,
    texturesWalls,
    textureFloor,
    dataObjects = null
  ) {
    return new Promise((resolve) => {
      this.room = new Room(shape, arrayWalls, texturesWalls, textureFloor);
      this.measures = new Measures();
      this.event = new EventManager();
      this.menuManager = new MenuManager();

      if (dataObjects !== null) {
        
        dataObjects.objects.forEach((objectData) => {
          const { type, scale, position, rotation, src, texture, subType, id } = objectData;

          let options = {
            modelPath: src || null,
            scale: scale
              ? new THREE.Vector3(scale.x, scale.y, scale.z)
              : undefined,
            position: position
              ? new THREE.Vector3(position.x, position.y, position.z)
              : undefined,
          };

          // Ajout des données spécifiques
          if (rotation) {
            options.rotation = new THREE.Euler(
              rotation._x || 0,
              rotation._y || 0,
              rotation._z || 0,
              rotation._order || "XYZ"
            );
          }
          if (texture) options.srcTexture = texture;
          if (subType) options.subType = subType;
          if (id) options.id = id;

          // Crée l'objet
          this.objectManager.createObject(type, options);
        });
      }

      // Simule une tâche longue (par ex. chargement de textures)
      setTimeout(() => {
        resolve(); // Indique que la création est terminée
      }, 1000);
    });
  }

  /**
   * Disable interactions when canvas is inactive
   */
  disableInteractions() {
    this.menuManager.removeTooltip();
    this.active = false;

    if (this.raycaster) {
      this.raycaster.objectsToCheck = [];
    }

    if (this.renderer) {
      this.renderer.instance.setAnimationLoop(null);
    }

    if (this.canvas) {
      this.canvas.style.display = "none";
    }
  }

  // Méthode pour réactiver les interactions
  enableInteractions() {
    this.active = true;

    // Remettre les objets à checker pour les raycasters
    if (this.raycaster) {
      this.raycaster.objectsToCheck = this.objectsToCheck;
    }

    // Réactiver le rendu
    if (this.renderer) {
      this.renderer.instance.setAnimationLoop();
    }

    // Afficher le canvas
    if (this.canvas) {
      this.canvas.style.display = "block";
    }
  }

  resizeCanvas() {
    this.resize(); // Force la mise à jour du canvas
  }

  normalizePointer(event) {
    const rect = this.canvas.getBoundingClientRect(); // Position et taille du canvas
    const pointer = {
      x: ((event.clientX - rect.left) / rect.width) * 2 - 1, // Normalisé entre -1 et 1
      y: -((event.clientY - rect.top) / rect.height) * 2 + 1, // Normalisé entre -1 et 1
    };

    return pointer;
  }

  resize() {
    this.camera.resize();
    this.renderer.resize();
    this.menuManager.resize();
  }

  update() {
    if (!this.active) return;
    this.camera.update();
    this.room.update();
    this.renderer.update();
  }

  destroy() {
    // Traverse the whole scene
    this.scene.traverse((child) => {
      // Test if it's a mesh
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();

        // Loop through the material properties
        for (const key in child.material) {
          const value = child.material[key];

          // Test if there is a dispose function
          if (value && typeof value.dispose === "function") {
            value.dispose();
          }
        }
      }
    });
  }
}
